import {
  Grid,
  Stack,
  MultiSelect,
  Box,
  Group,
  Text,
  Alert,
  Checkbox,
  Button,
  Modal,
  TextInput,
  Divider,
} from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconAlertTriangle, IconPlus } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useAccounts,
  useLabs,
  useSurgeryCenters,
  usePhysicians,
  useCheckClientExists,
} from "../../hooks/useReferenceData";
import {
  decodePracticeLabSelection,
  encodePracticeLabSelection,
  selectedPracticeLabValue,
} from "./practiceLabSelection";
import {
  CertaintyDateInput,
  CertaintySelect,
  CertaintyTextInput,
  CertaintyRichText,
} from "./CertaintyInput";
import { ReviewBadge } from "./ReviewBadge";
import SmartSuggestion from "./SmartSuggestion";
// Quick-add form components
import PracticeForm, {
  PracticeFormValues,
} from "../admin/practices/PracticeForm";
import PhysicianForm, {
  PhysicianFormValues,
} from "../admin/physicians/PhysicianForm";
import SurgeryCenterLabForm, {
  SurgeryCenterLabFormValues,
} from "../admin/surgeryCentersLabs/SurgeryCenterLabForm";
// Quick-add mutation hooks
import { useCreatePractice } from "../../hooks/admin/usePractices";
import { useCreatePhysician } from "../../hooks/admin/usePhysicians";
import { useCreateSurgeryCenterLab } from "../../hooks/admin/useSurgeryCentersLabs";
import { useCreateIcdCode } from "../../hooks/admin/useIcdCodes";
import { useCreateProcedure } from "../../hooks/admin/useProceduresAdmin";
import { isPracticeLabRequired } from "../../utils/accessioningClientRequirements";

const sanitizeOptions = <T extends { value: string | null | undefined }>(
  opts: T[] | undefined
): T[] => {
  if (!Array.isArray(opts)) return [];
  return opts.filter(
    (o) => typeof o.value !== "undefined" && o.value !== null && o.value !== ""
  );
};

interface CaseInfoFormProps {
  form: UseFormReturnType<any>;
  certainties?: Record<string, number>;
  onCertaintyChange?: (field: string, certainty: number) => void;
  onAutoCreatePracticePhysicianChange?: (value: boolean) => void;
  // Dropdown data - will be populated by custom hooks
  icdCodes?: Array<{ value: string; label: string }>;
  procedures?: Array<{ value: string; label: string }>;
  missingFields?: string[];
  unmatchedExtractions?: Record<string, string>;
  onClearUnmatched?: (field: string) => void;
  onRemoveUnmatched?: (field: string, value: string) => void;
  onExternalCaseNumberChange?: (value: string) => void;
  onExternalCaseNumberBlur?: () => void;
  externalCaseNumberStatus?: "idle" | "claiming" | "verified" | "conflict";
  accessioningType?: string;
}

export default function CaseInfoForm({
  form,
  certainties = {},
  onCertaintyChange,
  onAutoCreatePracticePhysicianChange,
  icdCodes = [],
  procedures = [],
  unmatchedExtractions = {},
  onClearUnmatched,
  onRemoveUnmatched,
  onExternalCaseNumberChange,
  onExternalCaseNumberBlur,
  externalCaseNumberStatus = "idle",
  accessioningType,
}: CaseInfoFormProps) {
  const [practicePhysicianWarning, setPracticePhysicianWarning] = useState<
    string | null
  >(null);
  const [autoCreatePracticePhysician, setAutoCreatePracticePhysician] =
    useState(false);

  // Quick-add modal states
  const [
    practiceModalOpened,
    { open: openPracticeModal, close: closePracticeModal },
  ] = useDisclosure(false);
  const [
    surgeryCenterModalOpened,
    { open: openSurgeryCenterModal, close: closeSurgeryCenterModal },
  ] = useDisclosure(false);
  const [
    physicianModalOpened,
    { open: openPhysicianModal, close: closePhysicianModal },
  ] = useDisclosure(false);
  const [
    icdCodeModalOpened,
    { open: openIcdCodeModal, close: closeIcdCodeModal },
  ] = useDisclosure(false);
  const [newIcdCode, setNewIcdCode] = useState("");
  const [newIcdDescription, setNewIcdDescription] = useState("");
  const [
    procedureModalOpened,
    { open: openProcedureModal, close: closeProcedureModal },
  ] = useDisclosure(false);
  const [newProcedureName, setNewProcedureName] = useState("");
  const [newProcedureDescription, setNewProcedureDescription] = useState("");

  // Quick-add mutations
  const createPractice = useCreatePractice();
  const createSurgeryCenter = useCreateSurgeryCenterLab();
  const createPhysician = useCreatePhysician();
  const createIcdCode = useCreateIcdCode();
  const createProcedure = useCreateProcedure();

  // Get practices, surgical centers, and physicians
  const { data: practices = [] } = useAccounts();
  const { data: labs = [] } = useLabs();
  const { data: surgeryCenters = [] } = useSurgeryCenters();
  const { data: physicians = [] } = usePhysicians();

  // Get form values for validation
  const practiceId = form.values.case?.accountId;
  const submittingLabId = form.values.case?.submittingLabId;
  const surgeryCenterLabId = form.values.case?.locationId;
  const physicianId = form.values.case?.physicianId;

  const practiceLabOptions = useMemo(
    () =>
      [
        ...sanitizeOptions(practices).map((practice) => ({
          value: encodePracticeLabSelection({
            type: "practice",
            id: practice.value,
          }),
          label: `${practice.label} — Practice`,
        })),
        ...sanitizeOptions(labs).map((lab) => ({
          value: encodePracticeLabSelection({ type: "lab", id: lab.value }),
          label: `${lab.label} — Lab`,
        })),
      ].sort((a, b) => a.label.localeCompare(b.label)),
    [labs, practices]
  );

  const surgeryCenterOptions = useMemo(() => {
    const currentLegacyLab = labs.find(
      (lab) => lab.value === surgeryCenterLabId
    );
    if (!currentLegacyLab) return surgeryCenters;
    return [
      ...surgeryCenters,
      {
        ...currentLegacyLab,
        label: `${currentLegacyLab.label} — Legacy lab selection`,
      },
    ];
  }, [labs, surgeryCenterLabId, surgeryCenters]);

  const handlePracticeLabChange = useCallback(
    (value: string | null) => {
      const selection = decodePracticeLabSelection(value);
      form.setFieldValue(
        "case.accountId",
        selection?.type === "practice" ? selection.id : ""
      );
      form.setFieldValue(
        "case.submittingLabId",
        selection?.type === "lab" ? selection.id : ""
      );
      onClearUnmatched?.("case.accountId");
      onClearUnmatched?.("case.submittingLabId");
    },
    [form, onClearUnmatched]
  );

  // Check if practice-physician combination exists
  // Using the existing hook which checks via checkPracticeCombinationExists
  const { data: combinationExists, isFetching: isCheckingCombination } =
    useCheckClientExists(practiceId, surgeryCenterLabId, physicianId);

  // Notify parent component when auto-create state changes
  useEffect(() => {
    onAutoCreatePracticePhysicianChange?.(autoCreatePracticePhysician);
  }, [autoCreatePracticePhysician, onAutoCreatePracticePhysicianChange]);

  // Check for existing practice-physician link when selections change
  useEffect(() => {
    // Don't evaluate until the check has completed - prevents flash
    if (isCheckingCombination) return;

    if (practiceId && physicianId) {
      if (combinationExists) {
        setPracticePhysicianWarning(null);
        setAutoCreatePracticePhysician(false);
      } else {
        setPracticePhysicianWarning(
          surgeryCenterLabId
            ? "This physician is not linked to the selected practice or surgical center."
            : "This physician is not linked to the selected practice."
        );
        setAutoCreatePracticePhysician(true);
      }
    } else {
      setPracticePhysicianWarning(null);
      setAutoCreatePracticePhysician(false);
    }
  }, [
    practiceId,
    physicianId,
    surgeryCenterLabId,
    combinationExists,
    isCheckingCombination,
  ]);

  // Quick-add handlers - sanitize empty strings to null for UUID/optional fields
  const handlePracticeSubmit = async (values: PracticeFormValues) => {
    const practice = await createPractice.mutateAsync({
      name: values.name,
      shortName: values.shortName || undefined,
      primaryPhone: values.primaryPhone || undefined,
      secondaryPhone: values.secondaryPhone || undefined,
      fax: values.fax || undefined,
      preferredPathologistId: values.preferredPathologistId || undefined,
    });
    form.setFieldValue("case.accountId", practice.id);
    form.setFieldValue("case.submittingLabId", "");
    closePracticeModal();
  };

  const isSurgeryCenterNameLinked = (name: string) =>
    surgeryCenters.some(
      (sc) =>
        sc.label.split(" (")[0].trim().toLowerCase() ===
        name.trim().toLowerCase()
    );

  const handleSurgeryCenterSubmit = async (
    values: SurgeryCenterLabFormValues
  ) => {
    if (isSurgeryCenterNameLinked(values.name)) {
      notifications.show({
        color: "red",
        title: "Duplicate",
        message: `"${values.name}" already exists as a surgical center.`,
      });
      return;
    }
    const surgeryCenter = await createSurgeryCenter.mutateAsync({
      name: values.name,
      type: "surgery_center",
      address: values.address || null,
      primaryPhone: values.primaryPhone || null,
      secondaryPhone: values.secondaryPhone || null,
      fax: values.fax || null,
      externalCaseNumberPrefix: values.externalCaseNumberPrefix || null,
      preferredPathologistId: values.preferredPathologistId || null,
      logoS3Key: values.logoS3Key || null,
    });
    form.setFieldValue("case.locationId", surgeryCenter.id);
    closeSurgeryCenterModal();
  };

  const handlePhysicianSubmit = async (values: PhysicianFormValues) => {
    // The physicians service already sanitizes input internally
    const physician = await createPhysician.mutateAsync(values);
    form.setFieldValue("case.physicianId", physician.id);
    closePhysicianModal();
  };

  return (
    <Stack gap="md">
      <Grid gutter="md">
        {/* Row 1: Practice | Physician */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Box>
            <CertaintySelect
              label="Practice or Lab"
              placeholder="Select practice or lab"
              required={isPracticeLabRequired(accessioningType)}
              showReviewBadge
              reviewTooltip="Verify practice"
              name="case.accountId"
              data={practiceLabOptions}
              searchable
              value={selectedPracticeLabValue(practiceId, submittingLabId)}
              error={form.errors["case.accountId"]}
              certainty={certainties["case.accountId"]}
              onCertaintyChange={(c) =>
                onCertaintyChange?.("case.accountId", c)
              }
              onChange={handlePracticeLabChange}
            />
            {unmatchedExtractions["case.submittingLabId"] &&
            !form.values.case?.accountId &&
            !form.values.case?.submittingLabId ? (
              <SmartSuggestion
                extractedValue={unmatchedExtractions["case.submittingLabId"]}
                onAdd={async () => {
                  const lab = await createSurgeryCenter.mutateAsync({
                    name: unmatchedExtractions["case.submittingLabId"],
                    type: "lab",
                  });
                  form.setFieldValue("case.accountId", "");
                  form.setFieldValue("case.submittingLabId", lab.id);
                  onClearUnmatched?.("case.submittingLabId");
                }}
                isLoading={createSurgeryCenter.isPending}
              />
            ) : unmatchedExtractions["case.accountId"] &&
              !form.values.case?.accountId &&
              !form.values.case?.submittingLabId ? (
              <SmartSuggestion
                extractedValue={unmatchedExtractions["case.accountId"]}
                onAdd={async () => {
                  const practice = await createPractice.mutateAsync({
                    name: unmatchedExtractions["case.accountId"],
                  });
                  form.setFieldValue("case.accountId", practice.id);
                  onClearUnmatched?.("case.accountId");
                }}
                isLoading={createPractice.isPending}
              />
            ) : (
              <Group justify="flex-end" mt={4}>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  leftSection={<IconPlus size={12} />}
                  onClick={openPracticeModal}
                >
                  Add New
                </Button>
              </Group>
            )}
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Box>
            <CertaintySelect
              label="Physician"
              placeholder="Select physician"
              required
              showReviewBadge
              reviewTooltip="Verify physician"
              name="case.physicianId"
              data={[
                ...sanitizeOptions(physicians),
                {
                  value: "__CREATE_NEW__",
                  label: "+ Create New Physician...",
                },
              ]}
              searchable
              {...form.getInputProps("case.physicianId")}
              onChange={(value) => {
                if (value === "__CREATE_NEW__") {
                  openPhysicianModal();
                  return;
                }
                form.setFieldValue("case.physicianId", value);
              }}
              certainty={certainties["case.physicianId"]}
              onCertaintyChange={(c) =>
                onCertaintyChange?.("case.physicianId", c)
              }
            />
            {unmatchedExtractions["case.physicianId"] &&
            !form.values.case?.physicianId ? (
              <SmartSuggestion
                extractedValue={unmatchedExtractions["case.physicianId"]}
                onAdd={async () => {
                  const name = unmatchedExtractions["case.physicianId"];
                  const parts = name.split(" ");
                  const firstName = parts[0] || name;
                  const lastName = parts.slice(1).join(" ") || "";
                  const physician = await createPhysician.mutateAsync({
                    firstName,
                    lastName,
                  });
                  form.setFieldValue("case.physicianId", physician.id);
                  onClearUnmatched?.("case.physicianId");
                }}
                isLoading={createPhysician.isPending}
              />
            ) : (
              <Group justify="flex-end" mt={4}>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  leftSection={<IconPlus size={12} />}
                  onClick={openPhysicianModal}
                >
                  Add New
                </Button>
              </Group>
            )}
          </Box>
        </Grid.Col>

        {/* Row 2: Surgical Center | ICD-10 Codes */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Box>
            <CertaintySelect
              label="Surgical Center"
              placeholder="Select surgical center"
              showReviewBadge
              reviewTooltip="Verify surgical center"
              name="case.locationId"
              data={[
                ...sanitizeOptions(surgeryCenterOptions),
                { value: "__CREATE_NEW__", label: "+ Create New Location..." },
              ]}
              searchable
              {...form.getInputProps("case.locationId")}
              onChange={(value) => {
                if (value === "__CREATE_NEW__") {
                  openSurgeryCenterModal();
                  return;
                }
                form.setFieldValue("case.locationId", value);
              }}
              certainty={certainties["case.locationId"]}
              onCertaintyChange={(c) =>
                onCertaintyChange?.("case.locationId", c)
              }
            />
            {unmatchedExtractions["case.locationId"] &&
            !form.values.case?.locationId ? (
              <SmartSuggestion
                extractedValue={unmatchedExtractions["case.locationId"]}
                onAdd={async () => {
                  const extractedName = unmatchedExtractions["case.locationId"];
                  if (isSurgeryCenterNameLinked(extractedName)) {
                    notifications.show({
                      color: "red",
                      title: "Duplicate",
                      message: `"${extractedName}" already exists as a surgical center.`,
                    });
                    return;
                  }
                  const sc = await createSurgeryCenter.mutateAsync({
                    name: extractedName,
                    type: "surgery_center",
                  });
                  form.setFieldValue("case.locationId", sc.id);
                  onClearUnmatched?.("case.locationId");
                }}
                isLoading={createSurgeryCenter.isPending}
              />
            ) : (
              <Group justify="flex-end" mt={4}>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  leftSection={<IconPlus size={12} />}
                  onClick={openSurgeryCenterModal}
                >
                  Add New
                </Button>
              </Group>
            )}
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Box>
            <Group gap={6} mb={4}>
              <Text size="sm" fw={500}>
                Clinical History Codes (ICD-10)
              </Text>
              <ReviewBadge tooltip="Review ICD codes carefully" />
            </Group>
            <MultiSelect
              label={undefined}
              placeholder="Search and select ICD codes"
              data={sanitizeOptions(icdCodes)}
              searchable
              {...form.getInputProps("case.clinicalHistoryCodes")}
            />
            {unmatchedExtractions["case.unmatchedIcdCodes"] && (
              <Stack gap={4} mt={4}>
                {unmatchedExtractions["case.unmatchedIcdCodes"]
                  .split(",")
                  .map((code) => (
                    <SmartSuggestion
                      key={code}
                      extractedValue={code}
                      onAdd={async () => {
                        const created = await createIcdCode.mutateAsync({
                          code: code.trim(),
                          description: code.trim(),
                        });
                        if (created?.id) {
                          const currentCodes =
                            form.values.case?.clinicalHistoryCodes || [];
                          form.setFieldValue("case.clinicalHistoryCodes", [
                            ...currentCodes,
                            created.id,
                          ]);
                        }
                        onRemoveUnmatched?.("case.unmatchedIcdCodes", code);
                      }}
                      isLoading={createIcdCode.isPending}
                    />
                  ))}
              </Stack>
            )}
            <Group justify="flex-end" mt={4}>
              <Button
                variant="subtle"
                size="compact-xs"
                leftSection={<IconPlus size={12} />}
                onClick={openIcdCodeModal}
              >
                Add New
              </Button>
            </Group>
          </Box>
        </Grid.Col>

        {/* Row 3: Procedure | Clinical History Notes */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Box>
            <CertaintySelect
              label="Procedure"
              placeholder="Select procedure"
              data={sanitizeOptions(procedures)}
              searchable
              required
              showReviewBadge
              reviewTooltip="Verify procedure matches the clinical context"
              name="case.procedureId"
              {...form.getInputProps("case.procedureId")}
              creatable
              creatableLabel="+ Use manual procedure"
              certainty={certainties["case.procedureId"]}
              onCertaintyChange={(c) =>
                onCertaintyChange?.("case.procedureId", c)
              }
            />
            {unmatchedExtractions["case.procedureId"] &&
            !form.values.case?.procedureId ? (
              <SmartSuggestion
                extractedValue={unmatchedExtractions["case.procedureId"]}
                onAdd={async () => {
                  const procedure = await createProcedure.mutateAsync({
                    name: unmatchedExtractions["case.procedureId"],
                  });
                  if (procedure?.id) {
                    form.setFieldValue("case.procedureId", procedure.id);
                  }
                  onClearUnmatched?.("case.procedureId");
                }}
                isLoading={createProcedure.isPending}
              />
            ) : (
              <Group justify="flex-end" mt={4}>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  leftSection={<IconPlus size={12} />}
                  onClick={openProcedureModal}
                >
                  Add New
                </Button>
              </Group>
            )}
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CertaintyRichText
            label="Clinical History"
            placeholder="Past medical history"
            showReviewBadge
            reviewTooltip="Review clinical history carefully"
            name="case.clinicalHistoryNotes"
            value={form.values.case?.clinicalHistoryNotes || ""}
            onChange={(val) =>
              form.setFieldValue("case.clinicalHistoryNotes", val)
            }
            certainty={certainties["case.clinicalHistoryNotes"]}
            onCertaintyChange={(c) =>
              onCertaintyChange?.("case.clinicalHistoryNotes", c)
            }
          />
        </Grid.Col>

        {/* Row 4: Indications | Impressions */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CertaintyRichText
            label="Indications"
            placeholder="Reason for procedure"
            showReviewBadge
            reviewTooltip="Review indications carefully"
            name="case.indications"
            value={form.values.case?.indications || ""}
            onChange={(val) => form.setFieldValue("case.indications", val)}
            certainty={certainties["case.indications"]}
            onCertaintyChange={(c) =>
              onCertaintyChange?.("case.indications", c)
            }
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CertaintyRichText
            label="Impressions"
            placeholder="Procedural findings"
            showReviewBadge
            reviewTooltip="Review impressions carefully"
            name="case.impressions"
            value={form.values.case?.impressions || ""}
            onChange={(val) => form.setFieldValue("case.impressions", val)}
            certainty={certainties["case.impressions"]}
            onCertaintyChange={(c) =>
              onCertaintyChange?.("case.impressions", c)
            }
          />
        </Grid.Col>

        {/* Row 5: External Case Number | (empty) */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CertaintyTextInput
            label="External Case Number (if applicable)"
            description={
              externalCaseNumberStatus === "claiming"
                ? "Verifying and reserving this case number..."
                : externalCaseNumberStatus === "verified"
                ? "Verified — this is now the saved case number"
                : "Case/specimen ID from the originating facility"
            }
            placeholder="e.g., SC-2024-1234"
            name="case.externalCaseNumber"
            {...form.getInputProps("case.externalCaseNumber")}
            {...(onExternalCaseNumberChange
              ? { onChange: onExternalCaseNumberChange }
              : {})}
            {...(onExternalCaseNumberBlur
              ? { onBlur: onExternalCaseNumberBlur }
              : {})}
            certainty={certainties["case.externalCaseNumber"]}
            onCertaintyChange={(c) =>
              onCertaintyChange?.("case.externalCaseNumber", c)
            }
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CertaintyTextInput
            label="Rule Outs"
            description="Differential diagnoses for the entire case (comma separated)"
            placeholder="e.g., Crohn's disease, IBD"
            name="case.ruleOuts"
            {...form.getInputProps("case.ruleOuts")}
            certainty={certainties?.["case.ruleOuts"]}
            onCertaintyChange={(c) => onCertaintyChange?.("case.ruleOuts", c)}
          />
        </Grid.Col>

        {/* Practice-Physician warning (full width) */}
        {practicePhysicianWarning && (
          <Grid.Col span={12}>
            <Alert
              icon={<IconAlertTriangle size={16} />}
              color="yellow"
              variant="light"
            >
              <Stack gap="xs">
                <Text size="sm">{practicePhysicianWarning}</Text>
                <Checkbox
                  label="Link physician to this practice"
                  description="A practice-physician relationship will be created automatically when saving the case"
                  checked={autoCreatePracticePhysician}
                  onChange={(e) =>
                    setAutoCreatePracticePhysician(e.currentTarget.checked)
                  }
                />
              </Stack>
            </Alert>
          </Grid.Col>
        )}

        {/* Row 5: Date Received | Date Collected */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CertaintyDateInput
            label="Date Received"
            placeholder="MM/DD/YYYY"
            description="When lab received the specimens"
            required
            showReviewBadge
            reviewTooltip="Verify date lab received the specimens"
            name="case.dateReceived"
            {...form.getInputProps("case.dateReceived")}
            certainty={certainties["case.dateReceived"]}
            onCertaintyChange={(c) =>
              onCertaintyChange?.("case.dateReceived", c)
            }
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CertaintyDateInput
            label="Date Collected (Date of Service)"
            placeholder="MM/DD/YYYY"
            description="When the procedure was performed"
            required
            showReviewBadge
            reviewTooltip="Verify date of service / procedure date"
            name="case.dateCollected"
            {...form.getInputProps("case.dateCollected")}
            certainty={certainties["case.dateCollected"]}
            onCertaintyChange={(c) =>
              onCertaintyChange?.("case.dateCollected", c)
            }
          />
        </Grid.Col>
      </Grid>

      {/* Quick-Add Practice Modal */}
      <Modal.Root
        opened={practiceModalOpened}
        onClose={closePracticeModal}
        size="md"
        closeOnClickOutside={false}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
          header: { flexShrink: 0 },
          body: { overflowY: "auto", flex: 1 },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Add New Practice</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <PracticeForm
              formId="practice-form-caseinfo"
              hideFooter
              onSubmit={handlePracticeSubmit}
            />
          </Modal.Body>
          <Box
            px="md"
            py="sm"
            style={{
              borderTop: "1px solid var(--mantine-color-default-border)",
              flexShrink: 0,
            }}
          >
            <Group justify="space-between">
              <Button
                variant="default"
                onClick={closePracticeModal}
                type="button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="practice-form-caseinfo"
                loading={createPractice.isPending}
              >
                Create
              </Button>
            </Group>
          </Box>
        </Modal.Content>
      </Modal.Root>

      {/* Quick-Add Surgical Center Modal */}
      <Modal.Root
        opened={surgeryCenterModalOpened}
        onClose={closeSurgeryCenterModal}
        size="xl"
        closeOnClickOutside={false}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Add New Surgery Center</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body style={{ flex: 1, overflow: "auto" }}>
            <SurgeryCenterLabForm
              formId="surgery-center-form-caseinfo"
              hideFooter
              onSubmit={handleSurgeryCenterSubmit}
              defaultType="surgery_center"
              fixedType="surgery_center"
            />
          </Modal.Body>
          <Divider />
          <Group justify="space-between" p="md">
            <Button
              variant="default"
              onClick={closeSurgeryCenterModal}
              type="button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="surgery-center-form-caseinfo"
              loading={createSurgeryCenter.isPending}
            >
              Create
            </Button>
          </Group>
        </Modal.Content>
      </Modal.Root>

      {/* Quick-Add Physician Modal */}
      <Modal.Root
        opened={physicianModalOpened}
        onClose={closePhysicianModal}
        size="md"
        closeOnClickOutside={false}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Add New Physician</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body style={{ flex: 1, overflow: "auto" }}>
            <PhysicianForm onSubmit={handlePhysicianSubmit} />
          </Modal.Body>
          <Divider />
          <Group justify="space-between" p="md">
            <Button variant="default" onClick={closePhysicianModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="physician-form"
              loading={createPhysician.isPending}
            >
              Create
            </Button>
          </Group>
        </Modal.Content>
      </Modal.Root>

      {/* Quick-Add ICD Code Modal */}
      <Modal
        opened={icdCodeModalOpened}
        onClose={() => {
          closeIcdCodeModal();
          setNewIcdCode("");
          setNewIcdDescription("");
        }}
        title="Add New ICD-10 Code"
        size="sm"
        closeOnClickOutside={false}
      >
        <Stack gap="sm">
          <TextInput
            label="ICD-10 Code"
            placeholder="e.g. K29.01"
            value={newIcdCode}
            onChange={(e) => setNewIcdCode(e.currentTarget.value)}
            required
          />
          <TextInput
            label="Description"
            placeholder="e.g. Acute gastritis with bleeding"
            value={newIcdDescription}
            onChange={(e) => setNewIcdDescription(e.currentTarget.value)}
            required
          />
          <Group justify="flex-end" mt="sm">
            <Button
              variant="default"
              onClick={() => {
                closeIcdCodeModal();
                setNewIcdCode("");
                setNewIcdDescription("");
              }}
            >
              Cancel
            </Button>
            <Button
              loading={createIcdCode.isPending}
              disabled={!newIcdCode.trim() || !newIcdDescription.trim()}
              onClick={async () => {
                const created = await createIcdCode.mutateAsync({
                  code: newIcdCode.trim(),
                  description: newIcdDescription.trim(),
                });
                if (created?.id) {
                  const currentCodes =
                    form.values.case?.clinicalHistoryCodes || [];
                  form.setFieldValue("case.clinicalHistoryCodes", [
                    ...currentCodes,
                    created.id,
                  ]);
                }
                closeIcdCodeModal();
                setNewIcdCode("");
                setNewIcdDescription("");
              }}
            >
              Create & Select
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Quick-Add Procedure Modal */}
      <Modal
        opened={procedureModalOpened}
        onClose={() => {
          closeProcedureModal();
          setNewProcedureName("");
          setNewProcedureDescription("");
        }}
        title="Add New Procedure"
        size="sm"
        closeOnClickOutside={false}
      >
        <Stack gap="sm">
          <TextInput
            label="Procedure Name"
            placeholder="e.g. Colonoscopy"
            value={newProcedureName}
            onChange={(e) => setNewProcedureName(e.currentTarget.value)}
            required
          />
          <TextInput
            label="Description"
            placeholder="e.g. Examination of the colon"
            value={newProcedureDescription}
            onChange={(e) => setNewProcedureDescription(e.currentTarget.value)}
          />
          <Group justify="flex-end" mt="sm">
            <Button
              variant="default"
              onClick={() => {
                closeProcedureModal();
                setNewProcedureName("");
                setNewProcedureDescription("");
              }}
            >
              Cancel
            </Button>
            <Button
              loading={createProcedure.isPending}
              disabled={!newProcedureName.trim()}
              onClick={async () => {
                const created = await createProcedure.mutateAsync({
                  name: newProcedureName.trim(),
                  description: newProcedureDescription.trim() || undefined,
                });
                if (created?.id) {
                  form.setFieldValue("case.procedureId", created.id);
                }
                closeProcedureModal();
                setNewProcedureName("");
                setNewProcedureDescription("");
              }}
            >
              Create & Select
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
